import { useState, useEffect, useRef } from 'react';
import { formatCurrency, parseCurrency, useEscapeClose } from '../../../utils/SamuiUtils';
import { getRowStyle } from '../../../utils/ModalKeyboardNavigation';
import { formatDate } from '../../../utils/DateUtils';
import DatePicker from '../DatePicker';
import moment from 'moment';

const INITIAL_LOAD_COUNT = 10;
const LOAD_MORE_COUNT = 10;

const SoModal = ({ showSoModal, handleSoClose, soDataList, onConfirmSoSelection, arId, initialSelectedItems }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [filteredSoDataList, setFilteredSoDataList] = useState([]);
    const [selectedRowIndex, setSelectedRowIndex] = useState(0);
    const [visibleCount, setVisibleCount] = useState(INITIAL_LOAD_COUNT);
    const tableContainerRef = useRef(null);

    const isSingleSelectMode = window.location.pathname === '/product-delivery';

    useEscapeClose(showSoModal, handleSoClose);

    useEffect(() => {
        if (showSoModal) {
            const selectedItemsArr = initialSelectedItems || [];
            setFilteredSoDataList(
                soDataList.filter((so) => {
                    const isAlreadySelected = selectedItemsArr.some((item) => item.Doc_No === so.Doc_No);
                    const matchesSearch = ((so.Doc_No || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                        (so.AR_Name || '').toLowerCase().includes(searchTerm.toLowerCase()));
                    const matchesArId = isSingleSelectMode ? true : parseCurrency(so.AR_ID) === parseCurrency(arId);

                    let matchesDate = true;
                    if (!isSingleSelectMode && startDate && endDate && so.Doc_Date) {
                        const docDate = new Date(so.Doc_Date);
                        docDate.setHours(docDate.getHours() - 7);

                        const docYear = docDate.getFullYear();
                        const docMonth = String(docDate.getMonth() + 1).padStart(2, '0');
                        const docDay = String(docDate.getDate()).padStart(2, '0');
                        const docDateFormatted = `${docYear}-${docMonth}-${docDay}`;

                        const startDateStr = typeof startDate === 'string' ? startDate : moment(startDate).format('YYYY-MM-DD');
                        const endDateStr = typeof endDate === 'string' ? endDate : moment(endDate).format('YYYY-MM-DD');

                        matchesDate = docDateFormatted >= startDateStr && docDateFormatted <= endDateStr;
                    }

                    return !isAlreadySelected && matchesSearch && matchesArId && matchesDate;
                })
            );
        }
    }, [searchTerm, startDate, endDate, soDataList, arId, showSoModal, initialSelectedItems, isSingleSelectMode]);

    useEffect(() => {
        setVisibleCount(INITIAL_LOAD_COUNT);
        setSelectedRowIndex(0);
    }, [filteredSoDataList]);

    useEffect(() => {
        if (showSoModal) {
            setSearchTerm('');
            setSelectedItems([]);
            setVisibleCount(INITIAL_LOAD_COUNT);
            setSelectedRowIndex(0);

            if (!isSingleSelectMode && soDataList.length > 0) {
                const dates = soDataList
                    .map((rec) => moment(rec.Doc_Date))
                    .filter((d) => d.isValid())
                    .sort((a, b) => b.valueOf() - a.valueOf());

                if (dates.length > 0) {
                    const latestDate = dates[0];
                    const firstDayOfMonth = moment(latestDate).startOf('month').format('YYYY-MM-DD');
                    const lastDayOfMonth = moment(latestDate).endOf('month').format('YYYY-MM-DD');

                    setStartDate(firstDayOfMonth);
                    setEndDate(lastDayOfMonth);
                }
            }
        }
    }, [showSoModal, soDataList, isSingleSelectMode]);

    const visibleSoDataList = filteredSoDataList.slice(0, visibleCount);
    const hasMoreItems = visibleSoDataList.length < filteredSoDataList.length;

    useEffect(() => {
        if (!showSoModal || !hasMoreItems) return;

        const tableContainer = tableContainerRef.current;
        if (!tableContainer) return;

        const canScroll = tableContainer.scrollHeight > tableContainer.clientHeight + 1;
        if (!canScroll) {
            setVisibleCount((prev) => Math.min(prev + LOAD_MORE_COUNT, filteredSoDataList.length));
        }
    }, [showSoModal, visibleCount, filteredSoDataList.length, hasMoreItems]);

    const loadMoreItems = () => {
        if (!hasMoreItems) return;

        setVisibleCount((prev) => Math.min(prev + LOAD_MORE_COUNT, filteredSoDataList.length));
    };

    const handleTableScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 24;

        if (isNearBottom) {
            loadMoreItems();
        }
    };

    const handleCheckboxChange = (so) => {
        if (isSingleSelectMode) {
            onConfirmSoSelection([so]);
            handleSoClose();
        } else {
            const updatedSelectedItems = selectedItems.some((item) => item.Doc_No === so.Doc_No)
                ? selectedItems.filter((item) => item.Doc_No !== so.Doc_No)
                : [...selectedItems, so];
            setSelectedItems(updatedSelectedItems);
        }
    };

    const handleConfirmSelection = () => {
        onConfirmSoSelection(selectedItems);
    };

    const clearSelection = () => {
        setSelectedItems([]);
    };

    return (
        <>
            <div className={`modal ${showSoModal ? 'show' : ''}`} style={{ display: showSoModal ? 'block' : 'none' }} tabIndex="-1" role="dialog">
                <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">รายการใบขาย</h5>
                            <button type="button" className="close" onClick={handleSoClose}>
                                <span>&times;</span>
                            </button>
                        </div>
                        <div className="modal-body" style={{ overflow: 'hidden' }}>
                            <div className="row">
                                <div className={isSingleSelectMode ? 'col-12' : 'col-6'}>
                                    <div className="form-group">
                                        <span className="fw-bold">ค้นหาเอกสาร</span>
                                        <input
                                            style={{ width: '100%' }}
                                            type="text"
                                            className="form-control"
                                            placeholder="ค้นหาใบขาย (SO)"
                                            value={searchTerm || ''}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>
                                {!isSingleSelectMode && (
                                    <>
                                        <div className="col-3">
                                            <div className="form-group">
                                                <span className="fw-bold">วันที่เริ่มต้น</span>
                                                <DatePicker
                                                    label=""
                                                    name="startDate"
                                                    value={startDate}
                                                    onChange={(date) => {
                                                        const year = date.year();
                                                        const month = String(date.month() + 1).padStart(2, '0');
                                                        const day = String(date.date()).padStart(2, '0');
                                                        setStartDate(`${year}-${month}-${day}`);
                                                    }}
                                                    disabled={false}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-3">
                                            <div className="form-group">
                                                <span className="fw-bold">วันที่สิ้นสุด</span>
                                                <DatePicker
                                                    label=""
                                                    name="endDate"
                                                    value={endDate}
                                                    onChange={(date) => {
                                                        const year = date.year();
                                                        const month = String(date.month() + 1).padStart(2, '0');
                                                        const day = String(date.date()).padStart(2, '0');
                                                        setEndDate(`${year}-${month}-${day}`);
                                                    }}
                                                    disabled={false}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div
                                className="table-responsive"
                                ref={tableContainerRef}
                                style={{ height: '50vh', overflowY: 'auto' }}
                                onScroll={handleTableScroll}
                            >
                                <table className="table table-striped table-hover">
                                    <thead className="thead-dark">
                                        <tr>
                                            {!isSingleSelectMode && (
                                                <th className="text-center" style={{ width: '1%' }}>
                                                    <input
                                                        type="checkbox"
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            setSelectedItems(checked ? filteredSoDataList : []);
                                                        }}
                                                        checked={
                                                            filteredSoDataList.length > 0 &&
                                                            selectedItems.length === filteredSoDataList.length
                                                        }
                                                    />
                                                </th>
                                            )}
                                            <th className="text-center" style={{ width: '15%' }}>เลขที่เอกสาร (SO)</th>
                                            <th className="text-center" style={{ width: '10%' }}>วันที่เอกสาร</th>
                                            <th className="text-center" style={{ width: '32%' }}>ชื่อลูกค้า</th>
                                            <th className="text-center" style={{ width: '32%' }}>หมายเลขอ้างอิงบิล</th>
                                            <th className="text-center" style={{ width: '10%' }}>ราคารวม</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleSoDataList.length > 0 ? (
                                            visibleSoDataList.map((so, index) => (
                                                <tr
                                                    key={so.Doc_ID || index + 1}
                                                    onClick={() => {
                                                        setSelectedRowIndex(index);
                                                        handleCheckboxChange(so);
                                                    }}
                                                    style={getRowStyle(index, selectedRowIndex)}
                                                >
                                                    {!isSingleSelectMode && (
                                                        <td className="text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedItems.some((item) => item.Doc_No === so.Doc_No)}
                                                                onChange={() => handleCheckboxChange(so)}
                                                                style={getRowStyle(index, selectedRowIndex)}
                                                            />
                                                        </td>
                                                    )}
                                                    <td className="text-center">{so.Doc_No}</td>
                                                    <td className="text-center">{formatDate(so.Doc_Date)}</td>
                                                    <td className="text-left">{so.AR_Name}</td>
                                                    <td className="text-left">{so.Ref_Bill_No}</td>
                                                    <td className="text-end">{formatCurrency(so.NetTotal)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={isSingleSelectMode ? 6 : 7}>
                                                    <center><h5>ไม่พบข้อมูล</h5></center>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                                {hasMoreItems && (
                                    <div className="py-2 text-center text-muted">
                                        เลื่อนลงสุดเพื่อโหลดเพิ่ม
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="modal-footer d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                                แสดง {Math.min(visibleSoDataList.length, filteredSoDataList.length)} จาก {filteredSoDataList.length} รายการ
                            </small>
                            {!isSingleSelectMode && (
                                <div>
                                    <button className="btn btn-primary me-2" onClick={handleConfirmSelection}>ยืนยันการเลือก</button>
                                    <button className="btn btn-danger" onClick={clearSelection}>ยกเลิกการเลือก</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SoModal;
